import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { apiClient, readError } from "@/lib/api";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarDays, Plus, Trash2, Layers, Hammer, UserPlus } from "lucide-react";
import { fr } from "date-fns/locale";
import { format } from "date-fns";
import { CATEGORIES } from "@/lib/categories";

const newLot = () => ({
  title: "",
  category: CATEGORIES[0],
  description: "",
  budget_min: "",
  budget_max: "",
  city: "",
  invited_artisans: [],
});

export default function NewChantier() {
  const nav = useNavigate();
  const [form, setForm] = useState({ title: "", description: "", city: "", deadline: null });
  const [lots, setLots] = useState([newLot()]);
  const [artisans, setArtisans] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get("/artisans");
        setArtisans(data.artisans || []);
      } catch {
        /* ignore */
      }
    })();
  }, []);

  const setLot = (idx, patch) =>
    setLots((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const removeLot = (idx) => setLots((prev) => prev.filter((_, i) => i !== idx));
  const addLot = () => setLots((prev) => [...prev, newLot()]);

  const toggleInvite = (idx, id) =>
    setLots((prev) =>
      prev.map((l, i) =>
        i === idx
          ? {
              ...l,
              invited_artisans: l.invited_artisans.includes(id)
                ? l.invited_artisans.filter((x) => x !== id)
                : [...l.invited_artisans, id],
            }
          : l,
      ),
    );

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        city: form.city,
        deadline: form.deadline ? format(form.deadline, "yyyy-MM-dd") : null,
        lots: lots.map((l) => ({
          title: l.title,
          category: l.category,
          description: l.description,
          budget_min: parseFloat(l.budget_min) || 0,
          budget_max: parseFloat(l.budget_max) || 0,
          city: l.city || form.city,
          deadline: form.deadline ? format(form.deadline, "yyyy-MM-dd") : null,
          invited_artisans: l.invited_artisans,
        })),
      };
      const { data } = await apiClient.post("/chantiers", payload);
      toast.success(`Chantier créé avec ${data.lots.length} lot(s)`);
      nav(`/chantiers/${data.chantier.id}`);
    } catch (e) {
      toast.error(readError(e));
    } finally {
      setBusy(false);
    }
  };

  const totalMin = lots.reduce((s, l) => s + (parseFloat(l.budget_min) || 0), 0);
  const totalMax = lots.reduce((s, l) => s + (parseFloat(l.budget_max) || 0), 0);

  return (
    <div className="min-h-screen bg-[#F7F7F9]">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00]">// Nouveau chantier multi-intervenants</div>
        <h1 className="display text-5xl mt-2 mb-2">Lancer un chantier complet</h1>
        <p className="text-zinc-600 max-w-2xl">
          Découpez votre projet en <strong>lots</strong> (gros œuvre, plomberie, électricité…). Chaque
          lot peut être confié à un artisan différent, avec son propre devis et son propre paiement séquestré.
        </p>

        <form onSubmit={submit} className="mt-10 space-y-6" data-testid="new-chantier-form">
          {/* Chantier global */}
          <div className="border-2 border-[#0A0A0B] bg-white p-8">
            <div className="flex items-center gap-2 mono text-[11px] uppercase tracking-widest text-zinc-500">
              <Layers className="w-4 h-4 text-[#FF4D00]" /> Projet
            </div>
            <div className="grid lg:grid-cols-3 gap-4 mt-4">
              <div className="lg:col-span-2">
                <label className="mono text-[11px] uppercase tracking-widest text-zinc-600">Nom du chantier</label>
                <input
                  required
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Ex: Rénovation maison Lyon 7e"
                  className="w-full mt-2 border-2 border-[#0A0A0B] px-4 py-3 bg-white"
                  data-testid="chantier-title"
                />
              </div>
              <div>
                <label className="mono text-[11px] uppercase tracking-widest text-zinc-600">Ville principale</label>
                <input
                  required
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  className="w-full mt-2 border-2 border-[#0A0A0B] px-4 py-3 bg-white"
                  data-testid="chantier-city"
                />
              </div>
            </div>
            <div className="grid lg:grid-cols-3 gap-4 mt-4">
              <div className="lg:col-span-2">
                <label className="mono text-[11px] uppercase tracking-widest text-zinc-600">Contexte global</label>
                <textarea
                  rows="3"
                  required
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Surface, état actuel, attentes, contraintes…"
                  className="w-full mt-2 border-2 border-[#0A0A0B] px-4 py-3 bg-white"
                  data-testid="chantier-description"
                />
              </div>
              <div>
                <label className="mono text-[11px] uppercase tracking-widest text-zinc-600">Livraison souhaitée</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="w-full mt-2 border-2 border-[#0A0A0B] px-4 py-3 bg-white text-left flex items-center justify-between"
                      data-testid="chantier-deadline"
                    >
                      <span className={form.deadline ? "text-[#0A0A0B]" : "text-zinc-400"}>
                        {form.deadline
                          ? format(form.deadline, "d MMMM yyyy", { locale: fr })
                          : "Sélectionner une date"}
                      </span>
                      <CalendarDays className="w-4 h-4 text-zinc-500" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="rounded-none border-2 border-[#0A0A0B] p-0" align="start">
                    <Calendar
                      mode="single"
                      locale={fr}
                      selected={form.deadline}
                      onSelect={(d) => setForm({ ...form, deadline: d })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>

          {/* Lots */}
          <div className="border-2 border-[#0A0A0B] bg-white">
            <div className="border-b-2 border-[#0A0A0B] px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Hammer className="w-5 h-5 text-[#FF4D00]" />
                <div className="display text-2xl">Lots du chantier ({lots.length})</div>
              </div>
              <button
                type="button"
                onClick={addLot}
                className="bg-[#FF4D00] text-white px-3 py-2 text-xs mono uppercase tracking-widest font-semibold hover:bg-[#E64500] flex items-center gap-1"
                data-testid="add-lot"
              >
                <Plus className="w-3.5 h-3.5" /> Ajouter un lot
              </button>
            </div>

            {lots.map((lot, idx) => (
              <div key={idx} className="border-b border-zinc-200 last:border-b-0 p-6" data-testid={`lot-${idx}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="mono text-[11px] uppercase tracking-widest text-zinc-500">
                    Lot #{String(idx + 1).padStart(2, "0")}
                  </div>
                  {lots.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeLot(idx)}
                      className="text-red-600 hover:text-red-800 flex items-center gap-1 text-xs mono uppercase tracking-widest"
                      data-testid={`remove-lot-${idx}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Retirer
                    </button>
                  )}
                </div>

                <div className="grid lg:grid-cols-12 gap-4">
                  <div className="lg:col-span-5">
                    <label className="mono text-[11px] uppercase tracking-widest text-zinc-600">Intitulé du lot</label>
                    <input
                      required
                      value={lot.title}
                      onChange={(e) => setLot(idx, { title: e.target.value })}
                      placeholder="Ex: Gros œuvre — fondations"
                      className="w-full mt-2 border-2 border-[#0A0A0B] px-4 py-3 bg-white"
                      data-testid={`lot-${idx}-title`}
                    />
                  </div>
                  <div className="lg:col-span-4">
                    <label className="mono text-[11px] uppercase tracking-widest text-zinc-600">Catégorie</label>
                    <select
                      value={lot.category}
                      onChange={(e) => setLot(idx, { category: e.target.value })}
                      className="w-full mt-2 border-2 border-[#0A0A0B] px-4 py-3 bg-white"
                      data-testid={`lot-${idx}-category`}
                    >
                      {CATEGORIES.map((c) => (
                        <option key={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div className="lg:col-span-3">
                    <label className="mono text-[11px] uppercase tracking-widest text-zinc-600">Ville (si différente)</label>
                    <input
                      value={lot.city}
                      onChange={(e) => setLot(idx, { city: e.target.value })}
                      placeholder={form.city || "Ville"}
                      className="w-full mt-2 border-2 border-[#0A0A0B] px-4 py-3 bg-white"
                      data-testid={`lot-${idx}-city`}
                    />
                  </div>
                </div>

                <div className="grid lg:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="mono text-[11px] uppercase tracking-widest text-zinc-600">Budget min (€)</label>
                    <input
                      type="number"
                      required
                      value={lot.budget_min}
                      onChange={(e) => setLot(idx, { budget_min: e.target.value })}
                      className="w-full mt-2 border-2 border-[#0A0A0B] px-4 py-3 bg-white"
                      data-testid={`lot-${idx}-budget-min`}
                    />
                  </div>
                  <div>
                    <label className="mono text-[11px] uppercase tracking-widest text-zinc-600">Budget max (€)</label>
                    <input
                      type="number"
                      required
                      value={lot.budget_max}
                      onChange={(e) => setLot(idx, { budget_max: e.target.value })}
                      className="w-full mt-2 border-2 border-[#0A0A0B] px-4 py-3 bg-white"
                      data-testid={`lot-${idx}-budget-max`}
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="mono text-[11px] uppercase tracking-widest text-zinc-600">Description du lot</label>
                  <textarea
                    rows="3"
                    required
                    value={lot.description}
                    onChange={(e) => setLot(idx, { description: e.target.value })}
                    className="w-full mt-2 border-2 border-[#0A0A0B] px-4 py-3 bg-white"
                    data-testid={`lot-${idx}-description`}
                  />
                </div>

                {/* Invitations */}
                <details className="mt-4 border-2 border-zinc-200">
                  <summary className="px-4 py-3 cursor-pointer flex items-center gap-2 mono text-[11px] uppercase tracking-widest hover:bg-zinc-50">
                    <UserPlus className="w-4 h-4 text-[#FF4D00]" />
                    Inviter des artisans ({lot.invited_artisans.length} sélectionné(s))
                  </summary>
                  <div className="p-4 border-t-2 border-zinc-200 grid sm:grid-cols-2 gap-2 max-h-56 overflow-auto scrollbar-thin">
                    {artisans.length === 0 && (
                      <div className="text-xs text-zinc-500 mono col-span-2">Aucun artisan inscrit pour l'instant.</div>
                    )}
                    {artisans.map((a) => (
                      <label
                        key={a.id}
                        className={`flex items-start gap-2 border p-2 cursor-pointer ${
                          lot.invited_artisans.includes(a.id) ? "border-[#FF4D00] bg-orange-50" : "border-zinc-200 hover:border-[#0A0A0B]"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={lot.invited_artisans.includes(a.id)}
                          onChange={() => toggleInvite(idx, a.id)}
                          className="mt-1 accent-[#FF4D00]"
                        />
                        <div className="flex-1 text-xs">
                          <div className="font-semibold flex items-center gap-1">
                            {a.name}
                            {a.is_premium && <span className="text-[#FF4D00] text-[10px] mono uppercase">★ Premium</span>}
                          </div>
                          <div className="text-zinc-500 mt-0.5">{(a.specialties || []).slice(0, 3).join(" · ") || "—"}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </details>
              </div>
            ))}
          </div>

          {/* Recap + submit */}
          <div className="border-2 border-[#0A0A0B] bg-[#0A0A0B] text-white p-6 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="mono text-[11px] uppercase tracking-widest text-zinc-400">
                Budget global estimé · {lots.length} lot(s)
              </div>
              <div className="display text-3xl mt-1">
                {totalMin.toLocaleString("fr-FR")} € — {totalMax.toLocaleString("fr-FR")} €
              </div>
            </div>
            <button type="submit" disabled={busy} className="btn-batisur" data-testid="chantier-submit">
              {busy ? "Publication…" : `Publier le chantier (${lots.length} lot${lots.length > 1 ? "s" : ""})`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
